import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import axios from 'axios';
import Button from '../../components/ui/Button';
import PasswordField from './PasswordField';
import { applyServerFieldErrors, formatTokenError } from '../../utils/errors';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
export const MIN_PASSWORD = 10;

/**
 * Body of every single-use-token password form (admin reset, candidate reset,
 * invite accept, signup verification).
 *
 * The submit is bound to a submitting flag: these tokens are consumed on first
 * use, so a double-click used to fire a second request that came back
 * "Invalid or expired token" just as the success redirect ran.
 *
 * @param {object} props
 * @param {string} props.token          token from the emailed link
 * @param {string} [props.endpoint]     path appended to the API base, e.g. '/auth/reset-password'
 * @param {(payload: {token: string, password: string}) => Promise<object>} [props.submit]
 *        use an API-client helper instead of the default POST to `endpoint`
 * @param {'reset'|'invite'|'verification'} [props.tokenKind]  drives the expired-link copy
 * @param {React.ReactNode} props.recovery  "request a new link" path shown when the token fails
 * @param {(data: object) => void} [props.onSuccess]  defaults to a redirect to `redirectTo`
 */
export default function SetPasswordForm({
  token,
  endpoint,
  submit,
  tokenKind = 'reset',
  recovery,
  submitLabel = 'Save password',
  successMessage = 'Password updated. You can sign in.',
  redirectTo,
  onSuccess,
  timeout = 20000,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const { register, handleSubmit, getValues, setError, formState: { errors } } = useForm();

  const onSubmit = async ({ password }) => {
    if (submitting) return;
    setSubmitting(true);
    setRequestError(null);
    try {
      const data = submit
        ? await submit({ token, password })
        : (await axios.post(`${API_BASE}${endpoint}`, { token, password }, { timeout })).data;
      toast.success(successMessage);
      if (onSuccess) onSuccess(data);
      else window.location.href = redirectTo;
    } catch (err) {
      const applied = applyServerFieldErrors(err, setError, { fields: ['password'] });
      if (!applied.length) {
        setRequestError(formatTokenError(err, tokenKind));
      }
      setSubmitting(false);
    }
  };

  return (
    <>
      {requestError && (
        <div
          role="alert"
          data-testid="set-password-error"
          className="mb-5 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3.5"
        >
          <p className="text-sm font-semibold text-danger-800">We couldn&apos;t set your password</p>
          <p className="mt-1 text-sm text-danger-700">{requestError}</p>
          {recovery && <div className="mt-3 text-sm">{recovery}</div>}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <PasswordField
          label={`New password (minimum ${MIN_PASSWORD} characters)`}
          autoComplete="new-password"
          autoFocus
          error={errors.password?.message}
          {...register('password', {
            required: 'Choose a password',
            minLength: { value: MIN_PASSWORD, message: `Use at least ${MIN_PASSWORD} characters` },
          })}
        />
        <PasswordField
          label="Confirm password"
          autoComplete="new-password"
          error={errors.confirm?.message}
          {...register('confirm', {
            required: 'Re-enter the password to confirm it',
            validate: value => value === getValues('password') || 'Both passwords must match',
          })}
        />
        <Button type="submit" className="w-full mt-2" loading={submitting}>
          {submitLabel}
        </Button>
      </form>
    </>
  );
}
